import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { Feature, Polygon } from "geojson";

import { Store } from "fond/types";

export type Selection = { status: "none" } | { status: "drawing" } | { status: "selected"; polygon: Feature<Polygon> };

/** A selection, tagged with the project it was drawn in. */
export type SelectionState = Selection & { projectId: string | null };

const initialState = { status: "none", projectId: null } as SelectionState;

const NO_SELECTION: Selection = { status: "none" };

/**
 * Redux slice storing the map selection polygon drawn via the legend's Select button.
 */
export const selectionSlice = createSlice({
  name: "selection",
  initialState,
  reducers: {
    startDrawing: (_, action: PayloadAction<string | null>): SelectionState => ({ status: "drawing", projectId: action.payload }),
    completeDrawing: (state, action: PayloadAction<Feature<Polygon>>): SelectionState => ({
      status: "selected",
      projectId: state.projectId,
      polygon: action.payload,
    }),
    clearSelection: (state): SelectionState => ({ status: "none", projectId: state.projectId }),
  },
});

export const { startDrawing, completeDrawing, clearSelection } = selectionSlice.actions;

/** The current selection, or none when it was drawn in a different project. */
export const selectSelectionState = (state: Store): Selection =>
  state.selection.projectId === state.project.projectId ? state.selection : NO_SELECTION;

/** The selection polygon, or null when none has been drawn. */
export const selectSelectionPolygon = (state: Store): Feature<Polygon> | null => {
  const selection = selectSelectionState(state);
  return selection.status === "selected" ? selection.polygon : null;
};
